import React from 'react';
import type { PendingTag } from './types';

interface TagInputDropdownProps {
  dropdownDismissed: boolean;
  showSuggestions: boolean;
  pendingTags: PendingTag[];
  suggestions: string[];
  isSuggestionsLoading: boolean;
  lastSegment: string;
  focusedSuggestionIndex: number;
  setFocusedSuggestionIndex: (idx: number) => void;
  allTags: string[];
  isTypedOnlyOption: boolean;
  showTypedFirst: boolean;
  handleSelectTypedOption: () => void;
  handleToggleSuggestion: (suggestion: string) => void;
}

const CheckIcon = () => (
  <svg width="12" height="9" viewBox="0 0 12 9" fill="none">
    <path
      d="M1 4.5L4 7.5L11 1"
      stroke="#FFFFFF"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface CheckboxProps {
  checked: boolean;
}

const Checkbox: React.FC<CheckboxProps> = ({ checked }) => (
  <div
    className={[
      'w-[18px] h-[18px] rounded-[4px] border-2 flex items-center justify-center shrink-0 transition-colors duration-150',
      checked ? 'bg-[#2A4455] border-[#2A4455]' : 'bg-white border-[#747778]',
    ].join(' ')}
  >
    {checked && <CheckIcon />}
  </div>
);

const TagInputDropdown: React.FC<TagInputDropdownProps> = ({
  dropdownDismissed,
  showSuggestions,
  pendingTags,
  suggestions,
  isSuggestionsLoading,
  lastSegment,
  focusedSuggestionIndex,
  setFocusedSuggestionIndex,
  allTags,
  showTypedFirst,
  handleSelectTypedOption,
  handleToggleSuggestion,
}) => {
  const isVisible =
    !dropdownDismissed &&
    (showSuggestions || pendingTags.length > 0) &&
    (suggestions.length > 0 || isSuggestionsLoading || lastSegment.length > 0);

  if (!isVisible) {
    return null;
  }

  const typedPendingNorm = lastSegment.toLowerCase();
  const isTypedPending = pendingTags.some(
    (t) => t.name.toLowerCase() === typedPendingNorm
  );

  return (
    <div className="absolute top-full left-0 right-0 z-50 mt-0.5 bg-white border border-[#DCDDE0] rounded-lg shadow-[0px_4px_12px_0px_#00000014] overflow-hidden">
      {isSuggestionsLoading ? (
        <div className="px-4 py-2.5 text-sm text-[#747778]">Ładowanie…</div>
      ) : suggestions.length > 0 ? (
        <ul role="listbox" className="max-h-48 overflow-y-auto py-1">
          {showTypedFirst && (
            <li
              role="option"
              aria-selected={focusedSuggestionIndex === 0}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelectTypedOption();
              }}
              onMouseEnter={() => setFocusedSuggestionIndex(0)}
              className={[
                'flex items-center gap-2.5 px-4 py-2 text-sm text-[#181A1B] cursor-pointer select-none',
                focusedSuggestionIndex === 0
                  ? 'bg-[#E8EEF2]'
                  : 'hover:bg-[#F2F3F5]',
              ].join(' ')}
            >
              <Checkbox checked={isTypedPending} />
              <span>{lastSegment}</span>
            </li>
          )}
          {suggestions.map((suggestion, idx) => {
            const listIdx = showTypedFirst ? idx + 1 : idx;
            const norm = suggestion.toLowerCase();
            const isConfirmed = allTags.some((t) => t.toLowerCase() === norm);
            const isChecked =
              isConfirmed ||
              pendingTags.some((t) => t.name.toLowerCase() === norm);
            return (
              <li
                key={suggestion}
                role="option"
                aria-selected={listIdx === focusedSuggestionIndex}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleToggleSuggestion(suggestion);
                }}
                onMouseEnter={() => setFocusedSuggestionIndex(listIdx)}
                className={[
                  'flex items-center gap-2.5 px-4 py-2 text-sm cursor-pointer select-none',
                  isConfirmed ? 'text-[#747778]' : 'text-[#181A1B]',
                  listIdx === focusedSuggestionIndex
                    ? 'bg-[#E8EEF2]'
                    : 'hover:bg-[#F2F3F5]',
                ].join(' ')}
              >
                <Checkbox checked={isChecked} />
                <span>{suggestion}</span>
                {isConfirmed && (
                  <span className="ml-auto text-xs text-[#747778]">dodano</span>
                )}
              </li>
            );
          })}
        </ul>
      ) : (
        /* No API match — show typed text as selectable option */
        <ul role="listbox" className="py-1">
          <li
            role="option"
            aria-selected={focusedSuggestionIndex === 0}
            onMouseDown={(e) => {
              e.preventDefault();
              handleSelectTypedOption();
            }}
            onMouseEnter={() => setFocusedSuggestionIndex(0)}
            className={[
              'flex items-center gap-2.5 px-4 py-2 text-sm text-[#181A1B] cursor-pointer select-none',
              focusedSuggestionIndex === 0
                ? 'bg-[#E8EEF2]'
                : 'hover:bg-[#F2F3F5]',
            ].join(' ')}
          >
            <Checkbox checked={isTypedPending} />
            <span>{lastSegment}</span>
          </li>
        </ul>
      )}
    </div>
  );
};

export default TagInputDropdown;
